import React, { useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useToasts } from 'react-toast-notifications';
import { useQuery } from 'react-query';
import {
  getAllServiceSummaries,
  createServiceSummary,
  updateServiceSummary,
} from '../api/serviceSummary';
import ServiceSummaryEditModal from '../components/ServiceSummaryEditModal.js';
import { format } from 'date-fns'; // date-fns'ten format fonksiyonunu içe aktar
import { getApiErrorMessage } from '../utils/apiError';

const ServiceSummaryPage = () => {
  const { data, error, isLoading, refetch } = useQuery(
    'serviceSummaries',
    getAllServiceSummaries,
    {
      refetchOnWindowFocus: false,
    }
  );
  const [selectedServiceSummary, setSelectedServiceSummary] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { addToast } = useToasts();

  const getStatusColor = status => {
    const red = Math.min(255, Math.round((1 - status / 100) * 255));
    const green = Math.min(255, Math.round((status / 100) * 255));
    return `rgb(${red}, ${green}, 0)`;
  };

  const getStatusBar = status => {
    const color = getStatusColor(status);
    return (
      <Box
        position="relative"
        width="100%"
        height="24px"
        borderRadius="md"
        bg="gray.200"
      >
        <Box
          position="absolute"
          height="100%"
          width={`${status}%`}
          bg={color}
          borderRadius="md"
          transition="width 0.3s ease-in-out, background-color 0.3s ease-in-out"
        />
        {status < 100 && (
          <Box
            position="absolute"
            height="100%"
            width={`${100 - status}%`}
            left={`${status}%`}
            bg="transparent"
            borderRadius="md"
          />
        )}
      </Box>
    );
  };

  // Tarih formatlama fonksiyonu
  const formatDate = dateString => {
    try {
      const date = new Date(dateString); // Tarih stringini Date nesnesine çevirin
      return format(date, 'dd/MM/yyyy HH:mm:ss'); // İstenilen formatta tarihi döndür
    } catch (error) {
      console.warn(`Invalid date format for: ${dateString}`, error);
      return 'Invalid date';
    }
  };

  const handleSave = async formData => {
    try {
      if (selectedServiceSummary) {
        await updateServiceSummary(selectedServiceSummary.id, formData);
        addToast('Service summary updated successfully', {
          appearance: 'success',
          autoDismiss: true,
        });
      } else {
        await createServiceSummary(formData);
        addToast('Service summary added successfully', {
          appearance: 'success',
          autoDismiss: true,
        });
      }
      await refetch();
    } catch (error) {
      console.error('Error saving service summary:', error);
      addToast(`Error saving service summary: ${getApiErrorMessage(error)}`, {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsEditModalOpen(false);
      setSelectedServiceSummary(null);
    }
  };

  if (isLoading) {
    return (
      <Box
        w="100%"
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box w="100%" display="flex" justifyContent="center" p={5}>
        <Text>An error occurred: {getApiErrorMessage(error)}</Text>
      </Box>
    );
  }

  return (
    <Box
      w="100%"
      h="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      p={4}
    >
      <Box
        flex="1"
        minHeight={0}
        display="flex"
        flexDirection="column"
        w="100%"
      >
        <TableContainer
          flex="1"
          minHeight={0}
          overflowY="auto"
          overflowX="auto"
          w="100%"
        >
          <Table variant="simple" colorScheme="gray" size="sm" w="100%">
            <Thead position="sticky" top={0} zIndex={1} bg="white">
              <Tr>
                <Th px={4} py={2}>
                  Parent
                </Th>
                <Th px={4} py={2}>
                  Instrument
                </Th>
                <Th px={4} py={2}>
                  Serial Number
                </Th>
                <Th px={4} py={2}>
                  Brand
                </Th>
                <Th px={4} py={2}>
                  Model
                </Th>
                <Th px={4} py={2}>
                  Status
                </Th>
                <Th px={4} py={2}>
                  Last Service
                </Th>
                <Th px={4} py={2}>
                  Next Service
                </Th>
                <Th px={4} py={2}>
                  Action
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {data && data.length > 0 ? (
                data.map(serviceSummary => (
                  <Tr key={serviceSummary.id} height="40px">
                    <Td px={4} py={2}>
                      {serviceSummary.parent}
                    </Td>
                    <Td px={4} py={2}>
                      {serviceSummary.instrument}
                    </Td>
                    <Td px={4} py={2}>
                      {serviceSummary.serialNumber}
                    </Td>
                    <Td px={4} py={2}>
                      {serviceSummary.brand}
                    </Td>
                    <Td px={4} py={2}>
                      {serviceSummary.model}
                    </Td>
                    <Td px={4} py={2}>
                      {getStatusBar(serviceSummary.status)}
                    </Td>
                    <Td px={4} py={2}>
                      {formatDate(serviceSummary.lastService)}
                    </Td>
                    <Td px={4} py={2}>
                      {formatDate(serviceSummary.nextService)}
                    </Td>
                    <Td px={4} py={2}>
                      <Flex align="center">
                        <Text
                          as="span"
                          mr={2}
                          cursor="pointer"
                          onClick={() => {
                            setIsEditModalOpen(true);
                            setSelectedServiceSummary(serviceSummary);
                          }}
                        >
                          Update
                        </Text>
                      </Flex>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={9}>
                    <Text textAlign="center">No service summaries found.</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {isEditModalOpen && (
        <ServiceSummaryEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          serviceSummary={selectedServiceSummary}
          onSave={handleSave}
        />
      )}
    </Box>
  );
};

export default ServiceSummaryPage;
